import { SharedAngular } from '@Client/@types/sharedAngular';
import { useService } from '@Client/runner.hooks/useService';
import DeleteModal from '@Client/runner.shared/modal/delete/DeleteModal';
import {
  Editor,
  EditorChangeEvent,
  EditorMountEvent
} from '@progress/kendo-react-editor';
import IProcedureActionDetail from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureActionDetail';
import React, { ChangeEvent, useEffect, useState } from 'react';

type Props = {
  index: number;
  action: IProcedureActionDetail;
  editorTools: unknown[];
  addEditorContentStyles: (event: EditorMountEvent) => void;
  persistChanges: (IProcedureActionDetail) => void;
};

const ProcedureActionEditor = (props: Props) => {
  const [editedAction, setEditedAction] = useState({ ...props.action });
  const [isEditMode, setIsEditMode] = useState(false);
  const [file, setFile] = useState('');
  const [isImageViewOpen, setIsImageViewOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const updateTitle = (e: ChangeEvent<HTMLInputElement>) => {
    setEditedAction({ ...editedAction, title: e.target.value });
  };
  const updateDescription = (e: EditorChangeEvent) => {
    setEditedAction({ ...editedAction, description: e.html });
  };

  const procedureApiService = useService<SharedAngular.ProcedureApiService>(
    'procedureApiService'
  );
  const fileService = useService<SharedAngular.FileService>('fileService');
  const notificationService = useService<SharedAngular.NotificationService>(
    'notificationService'
  );

  useEffect(() => {
    procedureApiService.updateProcedureAction(editedAction);
  }, [editedAction.modifiedFileId]);

  useEffect(() => {
    setTimeout(
      () => {
        fileService
          .getFileById(
            editedAction.modifiedFileId || editedAction.originalFileId
          )
          .then((response) => {
            const fileString = `data:${response.data.mimeType};base64,${response.data.data}`;
            setFile(fileString);
            if (!editedAction.modifiedFileId) {
              generateMaskedImage(response.data.filename, fileString);
            }
          });
      },
      // Stagger initial loading of files so we don't hammer the API
      file !== '' ? 0 : editedAction.order * 400
    );
  }, [editedAction.modifiedFileId, editedAction.originalFileId]);

  function generateMaskedImage(originalFileName: string, imageSrc: string) {
    const box = {
      x: editedAction.elementX,
      y: editedAction.elementY,
      width: editedAction.elementWidth,
      height: editedAction.elementHeight
    };

    if (box.x === 0 && box.y === 0) {
      box.x = editedAction.mouseX - 50;
      box.y = editedAction.mouseY - 50;
      box.height = box.width = 100;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const img = new Image();
    img.src = imageSrc;

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      const lineWidth = 6;

      ctx.drawImage(img, 0, 0);
      ctx.strokeStyle = 'orange';
      if (box.width > img.width * 0.5 || box.height > img.height * 0.5) {
        const radius = 100;
        drawCircleOverlay(
          ctx,
          canvas,
          editedAction.mouseX,
          editedAction.mouseY,
          radius
        );
        drawCircle(
          ctx,
          editedAction.mouseX,
          editedAction.mouseY,
          radius,
          lineWidth
        );
        const side = editedAction.mouseX > img.width / 2 ? 'left' : 'right';
        const x =
          side === 'left'
            ? editedAction.mouseX - radius
            : editedAction.mouseX + radius;
        drawCurvedArrow(ctx, x, editedAction.mouseY, side);
      } else {
        drawElementOverlay(ctx, canvas, box);
        drawElementOutline(ctx, box, lineWidth);
        const side = box.x > img.width - (box.x + box.width) ? 'left' : 'right';

        const x = side === 'left' ? box.x : box.x + box.width;
        const y = box.y + box.height / 2;
        drawCurvedArrow(ctx, x, y, side);
      }

      canvas.toBlob(
        (blob) => {
          const file = new File([blob], 'modified ' + originalFileName, {
            type: blob.type,
            lastModified: Date.now()
          });
          fileService
            .uploadFileToFolder(file, 'standardoperatingprocedures')
            .then((result) => {
              setEditedAction({
                ...editedAction,
                modifiedFileId: result.data
              });
              saveChanges();
            });
        },
        'image/jpeg',
        0.8
      );
    };
  }

  function drawElementOverlay(
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    box: { x: number; y: number; width: number; height: number }
  ) {
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.beginPath();
    ctx.rect(0, 0, canvas.width, canvas.height);
    ctx.rect(box.x, box.y, box.width, box.height);
    ctx.fill('evenodd');
  }

  function drawCircleOverlay(
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    x: number,
    y: number,
    radius: number
  ) {
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.beginPath();
    ctx.rect(0, 0, canvas.width, canvas.height);
    ctx.arc(x, y, radius, 0, 2 * Math.PI);
    ctx.fill('evenodd');
  }

  function drawCircle(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    radius: number,
    lineWidth: number
  ) {
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, 2 * Math.PI);
    ctx.stroke();
  }

  function drawElementOutline(
    ctx: CanvasRenderingContext2D,
    box: { x: number; y: number; width: number; height: number },
    lineWidth: number
  ) {
    ctx.lineWidth = lineWidth;
    ctx.strokeRect(
      box.x - lineWidth / 2,
      box.y - lineWidth / 2,
      box.width + lineWidth,
      box.height + lineWidth
    );
  }

  function drawCurvedArrow(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    side: 'left' | 'right'
  ) {
    x += side === 'left' ? -20 : 20;
    const offsetX = 100;
    const headLength = 30;

    const startX = side === 'left' ? x - offsetX : x + offsetX;
    const startY = y - 50;

    // Control point determines the curve
    const cpX = side === 'left' ? x - offsetX * 0.7 : x + offsetX * 0.7;
    const cpY = y + 10;

    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.quadraticCurveTo(cpX, cpY, x, y);
    ctx.stroke();

    const arrowHeadAngle = side === 'left' ? 0 : 3.14;

    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(
      x - headLength * Math.cos(arrowHeadAngle - Math.PI / 6),
      y - headLength * Math.sin(arrowHeadAngle - Math.PI / 6)
    );
    ctx.moveTo(x, y);
    ctx.lineTo(
      x - headLength * Math.cos(arrowHeadAngle + Math.PI / 6),
      y - headLength * Math.sin(arrowHeadAngle + Math.PI / 6)
    );
    ctx.stroke();
  }

  const saveChanges = () => {
    procedureApiService.updateProcedureAction(editedAction);
    props.persistChanges(editedAction);
    setIsEditMode(false);
  };

  const openDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const deleteAction = () => {
    procedureApiService
      .deleteProcedureAction(editedAction.procedureId, editedAction.id)
      .then(() => {
        notificationService.showSuccessToast('Action has been deleted.');
        setIsDeleteModalOpen(false);
        editedAction.active = false;
        props.persistChanges(editedAction);
        setIsEditMode(false);
      })
      .catch(() => {
        notificationService.showErrorToast(
          'An error occurred while deleting the Action.'
        );
      });
  };

  const cancelChanges = () => {
    setEditedAction({ ...props.action });
    setIsEditMode(false);
  };

  return (
    props.action &&
    (isEditMode ? (
      <div className="editor" id={'action_' + editedAction.id}>
        <div className="procedure-field">
          <label htmlFor="event-title">Title</label>
          <input
            type="text"
            id="procedure-title"
            value={editedAction.title}
            onChange={updateTitle}
          ></input>
        </div>
        <div className="procedure-field">
          <label>Description</label>
          <Editor
            tools={props.editorTools}
            contentStyle={{
              height: 140
            }}
            defaultContent={editedAction.description || ''}
            onChange={updateDescription}
            onMount={props.addEditorContentStyles}
            resizable={true}
            style={{ resize: 'vertical' }}
          />
        </div>
        <div className="editor-buttons">
          <button className="btn right" onClick={saveChanges}>
            Save
          </button>
          <button className="btn red right" onClick={openDeleteModal}>
            Delete
          </button>
          <button className="btn btn-text-only right" onClick={cancelChanges}>
            Cancel
          </button>
        </div>
        {file && (
          <div className="procedure-field">
            <img src={file}></img>
          </div>
        )}
        <DeleteModal
          title="Delete SOP Action"
          subject={editedAction.title}
          closeModal={() => setIsDeleteModalOpen(false)}
          isOpen={isDeleteModalOpen}
          deleteAction={deleteAction}
          confirmationWord="delete"
        ></DeleteModal>
      </div>
    ) : (
      <div className="editor-readonly" id={'action_' + editedAction.id}>
        <div
          className="editor-hover-button fa-light fa-pencil"
          title="Edit"
          onClick={() => setIsEditMode(true)}
        ></div>
        <div className="procedure-field">
          <div className="field-index">
            {props.index < 10 ? '0' : ''}
            {props.index}
          </div>
          <div className="field-title">{props.action.title}</div>
        </div>
        {props.action.description && (
          <div>
            <div
              dangerouslySetInnerHTML={{
                __html: props.action.description
              }}
            ></div>
          </div>
        )}
        {file && (
          <div
            className="procedure-field"
            onClick={() => setIsImageViewOpen(true)}
          >
            <img src={file}></img>
          </div>
        )}
        {isImageViewOpen && (
          <div
            className="procedure-image"
            onClick={() => setIsImageViewOpen(false)}
            title="Close"
          >
            <img src={file}></img>
          </div>
        )}
      </div>
    ))
  );
};

export default ProcedureActionEditor;
